import clsx from 'clsx';
import { cloneDeep } from 'lodash';
import { ChangeEvent, useEffect, useState } from 'react';
import { UploadFileTypeEnum } from 'src/generated/model';
import { useFileUpload } from 'src/hooks/useFileUpload';
import { Question } from 'src/types';
import { checkFileSizeLimit100MB } from 'src/util/file';
import { ImageObjectComponent } from '../ImageObjectComponent';
import { ButtonV2 } from '../common/ButtonV2';
import { Check } from '../common/Check';
import { ImageUpload } from '../common/ImageUpload';
import { TextareaV2 } from '../common/TextareaV2';
import { Typography } from '../common/Typography';
import { DocumentCard } from '../ib/DocumentCard';
import { ImageCard } from '../ib/ImageCard';
import SVGIcon from '../icon/SVGIcon';
import SolidSVGIcon from '../icon/SolidSVGIcon';

interface SuperSurveyComponentProps {
  surveyContent?: string;
  setContent: (content: any) => void;
  content: any;
  readOnly?: boolean;
  disabled?: boolean;
  id?: number;
}

export function SuperSurveyComponent({
  surveyContent,
  setContent,
  content = {},
  readOnly,
  disabled,
  id,
}: SuperSurveyComponentProps) {
  const [survey, setSurvey] = useState<Question[]>([]);
  const [loading, setLoading] = useState(false);
  const { handleUploadFile } = useFileUpload();

  useEffect(() => {
    if (surveyContent) {
      try {
        setSurvey(JSON.parse(surveyContent));
      } catch (err) {
        console.error(err);
      }
    }
  }, [surveyContent]);
  if (survey.length === 0) {
    return null;
  }

  return (
    <section className="flex flex-col gap-6 px-5 pb-10">
      {survey?.map((question) => {
        const onChangeValue = (value: string) => {
          const _content = cloneDeep(content);
          _content[question.id] = value;
          setContent(_content);
        };
        const onChangeCheckboxValue = (value: string) => {
          const _content = cloneDeep(content);
          if (_content[question.id]) {
            if (_content[question.id].includes(value)) {
              _content[question.id] = _content[question.id].filter((v: string) => v !== value);
            } else {
              _content[question.id].push(value);
            }
          } else {
            _content[question.id] = [value];
          }
          setContent(_content);
        };
        const handleImageAdd = async (e: ChangeEvent<HTMLInputElement>) => {
          e.stopPropagation();
          e.preventDefault();
          if (loading) return;
          if (!e.target.files) return;
          setLoading(true);
          const _content = cloneDeep(content);
          const uploadedImageName = await handleUploadFile(UploadFileTypeEnum['activityv3/images'], [
            e.target.files[0],
          ]);
          _content[question.id] = uploadedImageName[0];
          setContent(_content);
          setLoading(false);
        };
        const handleFileAdd = async (e: ChangeEvent<HTMLInputElement>) => {
          e.stopPropagation();
          e.preventDefault();
          if (loading) return;
          if (!e.target.files) return;
          setLoading(true);
          const _content = cloneDeep(content);
          if (e.target.files[0].type.includes('image')) {
            const uploadedImageName = await handleUploadFile(UploadFileTypeEnum['activityv3/images'], [
              e.target.files[0],
            ]);
            _content[question.id] = uploadedImageName[0];
          } else {
            const uploadedFileName = await handleUploadFile(UploadFileTypeEnum['activityv3/files'], [
              e.target.files[0],
            ]);
            _content[question.id] = uploadedFileName[0];
          }
          setContent(_content);
          setLoading(false);
        };
        const deleteFile = () => {
          const _content = cloneDeep(content);
          _content[question.id] = '';
          setContent(_content);
        };
        const value = content[question.id];
        switch (question.type) {
          case 'image':
            return (
              <div className="flex w-full flex-col gap-2" key={question.id}>
                <div className="flex flex-row items-center gap-1">
                  <Typography variant="title3" className="text-primary-orange-800">
                    {question.required ? '(필수)' : ''}
                  </Typography>
                  <Typography variant="title3" className="font-semibold">
                    {question.title}
                  </Typography>
                </div>
                {value && (
                  <ImageObjectComponent
                    id={question.id}
                    imageObjet={{ image: value, isDelete: false }}
                    {...(!readOnly && { onDeleteClick: deleteFile })}
                  />
                )}
                {!readOnly && <ImageUpload accept=".pdf, .png, .jpeg, .jpg" onChange={handleImageAdd} />}
              </div>
            );
          case 'file':
            return (
              <div className="flex w-full flex-col gap-2" key={question.id}>
                <div className="flex flex-row items-center gap-1">
                  <Typography variant="title3" className="text-primary-orange-800">
                    {question.required ? '(필수)' : ''}
                  </Typography>
                  <Typography variant="title3" className="font-semibold">
                    {question.title}
                  </Typography>
                </div>
                <div className="flex flex-col gap-2">
                  {!value && (
                    <label htmlFor="file-upload" className="inline-block cursor-pointer">
                      <ButtonV2
                        variant="outline"
                        color="gray400"
                        size={40}
                        className="flex items-center gap-1"
                        disabled={disabled}
                        onClick={() => {
                          const fileInput = document.getElementById('file-upload') as HTMLInputElement;
                          if (fileInput) {
                            fileInput.click();
                          }
                        }}
                      >
                        <SVGIcon.Plus size={16} weight="bold" color="gray700" />
                        <Typography variant="body2" className="font-medium">
                          파일첨부
                        </Typography>
                      </ButtonV2>
                      <input
                        type="file"
                        multiple
                        className="sr-only"
                        id="file-upload"
                        onChange={(e) => {
                          if (!e.target.files || e.target.files.length === 0) return;
                          if (!checkFileSizeLimit100MB([...e.target.files])) {
                            return alert('100MB가 넘는 파일은 첨부할 수 없습니다.');
                          }
                          handleFileAdd(e);
                        }}
                      />
                    </label>
                  )}

                  {value && (value.includes('images') || value.includes('files')) && (
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-row gap-2 overflow-x-auto">
                        {value.includes('images') && (
                          <ImageCard
                            id={question.id}
                            imageObjet={{ image: value, isDelete: false }}
                            onDeleteClick={deleteFile}
                          />
                        )}
                      </div>
                      {value.includes('files') && (
                        <DocumentCard
                          key={question.id}
                          id={question.id}
                          documentObjet={{ document: value, isDelete: false }}
                          onDeleteClick={deleteFile}
                        />
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-1">
                    <SolidSVGIcon.Info size={16} color="gray400" />
                    <Typography variant="caption" className="text-primary-gray-500">
                      100MB 이하 이미지/문서 파일 첨부 가능
                    </Typography>
                  </div>
                </div>
              </div>
            );
          case 'text':
            return (
              <div className="flex w-full flex-col gap-2" key={question.id}>
                <div className="flex flex-row items-center gap-1">
                  <Typography variant="title3" className="text-primary-orange-800">
                    {question.required ? '(필수)' : ''}
                  </Typography>
                  <Typography variant="title3" className="font-semibold">
                    {question.title}
                  </Typography>
                </div>
                <TextareaV2
                  onChange={(e) => onChangeValue(e.target.value)}
                  value={content[question.id]}
                  readOnly={readOnly}
                  disabled={disabled}
                  placeholder="내용을 입력해주세요."
                  className="h-20"
                />
              </div>
            );
          case 'longtext':
            return (
              <div className="flex w-full flex-col gap-2" key={question.id}>
                <div className="flex flex-row items-center gap-1">
                  <Typography variant="title3" className="text-primary-orange-800">
                    {question.required ? '(필수)' : ''}
                  </Typography>
                  <Typography variant="title3" className="font-semibold">
                    {question.title}
                  </Typography>
                </div>
                <TextareaV2
                  className="h-40"
                  onChange={(e) => onChangeValue(e.target.value)}
                  value={content[question.id]}
                  placeholder="내용을 입력해주세요."
                  readOnly={readOnly}
                  disabled={disabled}
                />
              </div>
            );
          case 'checkbox':
            return (
              <div className="flex w-full flex-col gap-2" key={question.id}>
                <div className="flex flex-row items-center gap-1">
                  <Typography variant="title3" className="text-primary-orange-800">
                    {question.required ? '(필수)' : ''}
                  </Typography>
                  <Typography variant="title3" className="font-semibold">
                    {question.title}
                  </Typography>
                </div>
                {question?.choices?.map((c: any, index: number) => (
                  <div
                    key={index}
                    className={`flex w-full cursor-pointer flex-row items-center gap-4 rounded-xl border border-primary-gray-200 p-4 shadow-[0_4px_8px_0_#F4F6F8] ${
                      value?.includes(c) && 'border-primary-orange-400 shadow-[0_4px_8px_0_#ffe8db]'
                    } ${readOnly && 'bg-primary-gray-100'} overflow-hidden`}
                    onClick={(e) => {
                      if (!readOnly) {
                        e.preventDefault();
                        onChangeCheckboxValue(c);
                      }
                    }}
                  >
                    <div className="flex flex-1 flex-col">
                      <Typography variant="title3">{c}</Typography>
                    </div>
                    <Check.Basic
                      name={String(question.id)}
                      checked={value?.includes(c) || false}
                      readOnly={readOnly}
                      onChange={() => {
                        onChangeCheckboxValue(c);
                      }}
                    />
                  </div>
                ))}
              </div>
            );
          case 'radiobutton':
            return (
              <div className="flex flex-col gap-2" key={question.id}>
                <div className="flex flex-row items-center gap-1">
                  <Typography variant="title3" className="text-primary-orange-800">
                    {question.required ? '(필수)' : ''}
                  </Typography>
                  <Typography variant="title3" className="font-semibold">
                    {question.title}
                  </Typography>
                </div>
                <div className="flex flex-col gap-3">
                  {question?.choices?.map((c: any, index: number) => (
                    <label
                      key={index}
                      onClick={() => onChangeValue(c)}
                      className={`flex w-full cursor-pointer flex-row items-center gap-4 rounded-xl border border-primary-gray-200 p-4 shadow-[0_4px_8px_0_#F4F6F8] ${
                        value === c && 'border-primary-orange-400 shadow-[0_4px_8px_0_#ffe8db]'
                      } ${readOnly && 'bg-primary-gray-100'} overflow-hidden`}
                    >
                      <div className="flex flex-1 flex-col">
                        <Typography variant="title3">{c}</Typography>
                      </div>
                      <span
                        className={clsx('flex items-center justify-center rounded-full border-2 transition-colors', {
                          'border-primary-gray-200 bg-white': !readOnly,
                          'border-primary-gray-200 bg-primary-gray-100': readOnly,
                          'cursor-not-allowed': readOnly,
                        })}
                        style={{ width: 24, height: 24 }}
                      >
                        {value === c && (
                          <span
                            className={clsx('rounded-full', {
                              'bg-primary-orange-800': !readOnly,
                              'bg-primary-gray-300': readOnly,
                            })}
                            style={{
                              width: `${24 * 0.5}px`,
                              height: `${24 * 0.5}px`,
                            }}
                          />
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            );
        }
      })}
    </section>
  );
}
